import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, AlertTriangle, XCircle, RefreshCw, Download, Bell } from 'lucide-react';

const Compliance = () => {
  const [complianceData, setComplianceData] = useState({
    overview: {},
    frameworks: [],
    records: [],
    alerts: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const getStatusIcon = (status) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'non-compliant':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Shield className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'compliant':
        return 'text-green-400';
      case 'warning':
        return 'text-yellow-400';
      case 'non-compliant':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  // Load compliance data on component mount
  useEffect(() => {
    loadComplianceData();
  }, []);

  const loadComplianceData = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Load overview
      const overviewResponse = await fetch('/compliance/overview', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (overviewResponse.ok) {
        const overviewData = await overviewResponse.json();
        setComplianceData(prev => ({ ...prev, overview: overviewData.overview }));
      }
      
      // Load frameworks
      const frameworksResponse = await fetch('/compliance/frameworks', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (frameworksResponse.ok) {
        const frameworksData = await frameworksResponse.json();
        setComplianceData(prev => ({ ...prev, frameworks: frameworksData.frameworks }));
      }
      
      // Load records
      const recordsResponse = await fetch('/compliance/records', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (recordsResponse.ok) {
        const recordsData = await recordsResponse.json();
        setComplianceData(prev => ({ ...prev, records: recordsData.records }));
      }
      
      // Load alerts
      const alertsResponse = await fetch('/compliance/alerts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (alertsResponse.ok) {
        const alertsData = await alertsResponse.json();
        setComplianceData(prev => ({ ...prev, alerts: alertsData.alerts }));
      }
      
    } catch (error) {
      console.error('Failed to load compliance data:', error);
      setMessage('Failed to load compliance data');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/compliance/reports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ format: 'json' }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create and download the report
        const blob = new Blob([JSON.stringify(data.report, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compliance-report-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setMessage('Compliance report generated and downloaded successfully');
        setMessageType('success');
      } else {
        const errorData = await response.json();
        setMessage(errorData.error || 'Failed to generate report');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
      setMessageType('error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Compliance Management</h1>
          <p className="text-gray-400 mt-1">Monitor and manage regulatory compliance across all frameworks</p>
        </div>
        <div className="flex items-center space-x-2">
          <button 
            onClick={loadComplianceData}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button 
            onClick={handleGenerateReport}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Generate Report
          </button>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-4 rounded-lg ${
          messageType === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
          messageType === 'error' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
          'bg-blue-500/20 text-blue-400 border border-blue-500/30'
        }`}>
          {message}
        </div>
      )}

      {/* Compliance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Overall Compliance</p>
              <p className="text-2xl font-bold text-white">
                {isLoading ? '...' : `${complianceData.overview.compliance_percentage || 0}%`}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-green-400">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span>
                {isLoading ? 'Loading...' : `${complianceData.overview.compliant || 0} of ${complianceData.overview.total_frameworks || 0} compliant`}
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Active Frameworks</p>
              <p className="text-2xl font-bold text-white">
                {isLoading ? '...' : complianceData.overview.total_frameworks || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-blue-400">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span>
                {isLoading ? 'Loading...' : `${complianceData.overview.compliant || 0} compliant, ${complianceData.overview.warning || 0} warnings`}
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Active Alerts</p>
              <p className="text-2xl font-bold text-white">
                {isLoading ? '...' : complianceData.alerts.length || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <Bell className="w-6 h-6 text-orange-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-orange-400">
              <Bell className="w-4 h-4 mr-1" />
              <span>
                {isLoading ? 'Loading...' : `${complianceData.alerts.filter(a => a.type === 'critical').length || 0} critical, ${complianceData.alerts.filter(a => a.type === 'warning').length || 0} warnings`}
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Compliance Frameworks */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Compliance Frameworks</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {isLoading ? (
            // Loading skeleton
            Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="p-4 bg-gray-700 rounded-lg border border-gray-600 animate-pulse">
                <div className="h-4 bg-gray-600 rounded mb-3"></div>
                <div className="h-3 bg-gray-600 rounded mb-2"></div>
                <div className="h-2 bg-gray-600 rounded"></div>
              </div>
            ))
          ) : complianceData.frameworks.length > 0 ? (
            complianceData.frameworks.map((framework, index) => (
              <motion.div
                key={framework.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="p-4 bg-gray-700 rounded-lg border border-gray-600 hover:border-gray-500 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-white">{framework.name}</h3>
                  <Shield className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-400">
                    {framework.risk_level || 'Medium'}
                  </span>
                  <span className="text-sm text-gray-400">
                    v{framework.version || '1.0'}
                  </span>
                </div>
                <div className="mt-2">
                  <p className="text-xs text-gray-400 line-clamp-2">
                    {framework.description || 'No description available'}
                  </p>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="col-span-full text-center py-8">
              <p className="text-gray-400">No compliance frameworks available</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Compliance Alerts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Compliance Alerts</h2>
        <div className="space-y-4">
          {isLoading ? (
            // Loading skeleton
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="p-3 bg-gray-700 rounded-lg animate-pulse">
                <div className="h-4 bg-gray-600 rounded mb-2"></div>
                <div className="h-3 bg-gray-600 rounded"></div>
              </div>
            ))
          ) : complianceData.alerts.length > 0 ? (
            complianceData.alerts.slice(0, 5).map((alert, index) => (
              <div key={index} className={`flex items-center justify-between p-3 rounded-lg border ${
                alert.type === 'critical' ? 'bg-red-500/20 border-red-500/30' :
                alert.type === 'warning' ? 'bg-yellow-500/20 border-yellow-500/30' :
                'bg-blue-500/20 border-blue-500/30'
              }`}>
                <div className="flex items-center space-x-3">
                  {alert.type === 'critical' ? (
                    <XCircle className="w-5 h-5 text-red-400" />
                  ) : alert.type === 'warning' ? (
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  ) : (
                    <Bell className="w-5 h-5 text-blue-400" />
                  )}
                  <div>
                    <p className="text-white font-medium">{alert.message}</p>
                    <p className="text-sm text-gray-400">
                      {alert.framework} • {alert.days_until ? `${alert.days_until} days` : 'Due soon'}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  alert.type === 'critical' ? 'bg-red-500/20 text-red-400' :
                  alert.type === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-blue-500/20 text-blue-400'
                }`}>
                  {alert.type}
                </span>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-gray-400">No active compliance alerts</p>
              <p className="text-sm text-gray-500 mt-1">All frameworks are compliant</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Compliance;
